import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Typography, Spin, Alert, Table, Tag, Button, Tooltip, message,
  Form, Input, Select, Popconfirm, Divider,
} from 'antd';
import {
  TeamOutlined, CrownOutlined, MailOutlined, DollarOutlined,
  RiseOutlined, WarningOutlined, UserOutlined, SyncOutlined,
  CheckCircleOutlined, ClockCircleOutlined, UnorderedListOutlined, SendOutlined,
  BellOutlined, DeleteOutlined, PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const StatCard = ({ title, value, prefix, icon, color, suffix }) => {
  const { token: tk } = antdTheme.useToken();
  return (
    <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Statistic
          title={<span style={{ color: tk.colorTextSecondary, fontSize: 13 }}>{title}</span>}
          value={value}
          prefix={prefix}
          suffix={suffix}
          valueStyle={{ color, fontWeight: 700, fontSize: 28 }}
        />
        <div style={{
          width: 44, height: 44,
          borderRadius: 10,
          background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {React.cloneElement(icon, { style: { fontSize: 20, color } })}
        </div>
      </div>
    </Card>
  );
};

const PLATFORM_LISTS_ORDER = ['platform_all', 'platform_free', 'platform_pro'];

const PLAN_LIST_CONFIG = {
  platform_all:  { label: 'All Users',  color: '#0a40d9', bg: '#e1e9ff', icon: <TeamOutlined /> },
  platform_free: { label: 'Free Users', color: '#6b7280', bg: '#f3f4f6', icon: <UserOutlined /> },
  platform_pro:  { label: 'Pro Users',  color: '#f59e0b', bg: '#fef3c7', icon: <CrownOutlined /> },
};

const AdminDashboard = () => {
  const { token: tk }     = antdTheme.useToken();
  const [stats, setStats] = useState(null);
  const [recentOrgs, setRecentOrgs] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [platformLists, setPlatformLists]   = useState([]);
  const [listsLoading, setListsLoading]     = useState(true);
  const [syncing, setSyncing]               = useState(false);

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifList,    setNotifList]    = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSending, setNotifSending] = useState(false);
  const [notifForm]                     = Form.useForm();

  const fetchAdminNotifs = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await axios.get('/api/admin/notifications', { headers: authHeader() });
      setNotifList(res.data || []);
    } catch { /* silent */ }
    setNotifLoading(false);
  }, []);

  const sendNotification = async (values) => {
    setNotifSending(true);
    try {
      await axios.post('/api/admin/notifications', values, { headers: authHeader() });
      message.success('Notification broadcast sent');
      notifForm.resetFields();
      fetchAdminNotifs();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to send');
    }
    setNotifSending(false);
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`/api/admin/notifications/${id}`, { headers: authHeader() });
      setNotifList(prev => prev.filter(n => n.id !== id));
      message.success('Notification deleted');
    } catch {
      message.error('Delete failed');
    }
  };

  const navigate = useNavigate();

  const loadPlatformLists = useCallback(async (silent = false) => {
    if (!silent) setListsLoading(true);
    try {
      const res = await axios.get('/api/admin/platform-lists', { headers: authHeader() });
      setPlatformLists(res.data);
    } catch { /* non-fatal */ }
    finally { setListsLoading(false); }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/admin/platform-lists/sync', {}, { headers: authHeader() });
      message.success(`Synced ${res.data.lists.map(l => `${l.name} (${l.subscriber_count})`).join(', ')}`);
      loadPlatformLists(true);
    } catch (err) {
      message.error(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, orgsRes] = await Promise.all([
          axios.get('/api/admin/stats', { headers: authHeader() }),
          axios.get('/api/admin/orgs',  { headers: authHeader() }),
        ]);
        setStats(statsRes.data);
        // Show 5 most recent signups
        setRecentOrgs(orgsRes.data.slice(0, 5));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load platform stats');
      } finally {
        setLoading(false);
      }
    };
    load();
    loadPlatformLists();
    fetchAdminNotifs();
  }, [fetchAdminNotifs]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spin size="large" />
    </div>
  );

  if (error) return <Alert type="error" message={error} showIcon style={{ borderRadius: 10 }} />;

  const planColor = { free: 'default', pro: 'gold' };

  const recentCols = [
    { title: 'Organisation', dataIndex: 'name', key: 'name',
      render: (name, row) => (
        <span style={{ cursor: 'pointer', color: tk.colorPrimary }}
          onClick={() => navigate(`/admin/orgs/${row.id}`)}>
          {name}
        </span>
      ),
    },
    { title: 'Owner', dataIndex: 'owner_email', key: 'owner_email',
      render: v => <Text style={{ color: tk.colorTextSecondary, fontSize: 13 }}>{v}</Text>,
    },
    { title: 'Plan', dataIndex: 'plan', key: 'plan',
      render: p => <Tag color={planColor[p] || 'default'}>{p?.toUpperCase()}</Tag>,
    },
    { title: 'Signed up', dataIndex: 'created_at', key: 'created_at',
      render: v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Platform Overview</Title>
          <Text style={{ color: tk.colorTextSecondary }}>SES Mailbox — Super Admin</Text>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => navigate('/admin/orgs')}>All Organisations</Button>
          <Button onClick={() => navigate('/admin/users')}>All Users</Button>
        </div>
      </div>

      {/* Stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Total Organisations"
            value={stats.total_orgs}
            icon={<TeamOutlined />}
            color="#0a40d9"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Pro Subscribers"
            value={stats.pro_orgs}
            icon={<CrownOutlined />}
            color="#f59e0b"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Monthly Recurring Revenue"
            value={stats.mrr.toFixed(2)}
            prefix="$"
            icon={<DollarOutlined />}
            color="#10b981"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Emails Sent Today"
            value={stats.emails_sent_today.toLocaleString()}
            icon={<MailOutlined />}
            color="#3b82f6"
          />
        </Col>
      </Row>

      {/* Secondary stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        <Col xs={24} sm={8}>
          <StatCard
            title="Free Orgs"
            value={stats.free_orgs}
            icon={<UserOutlined />}
            color="#2e6bf5"
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title="New Signups This Week"
            value={stats.new_signups_this_week}
            icon={<RiseOutlined />}
            color="#06b6d4"
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            title="Suspended Orgs"
            value={stats.suspended_orgs}
            icon={<WarningOutlined />}
            color={stats.suspended_orgs > 0 ? '#ef4444' : '#9ca3af'}
          />
        </Col>
      </Row>

      {/* Platform Email Lists */}
      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 24 }}
        styles={{ body: { padding: '20px 24px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UnorderedListOutlined style={{ color: '#0a40d9', fontSize: 18 }} />
              <Text strong style={{ fontSize: 16 }}>Platform Email Lists</Text>
            </div>
            <Text style={{ fontSize: 12, color: tk.colorTextSecondary }}>
              Auto-managed lists of your platform users — use them in campaigns to reach all or specific plan users.
            </Text>
          </div>
          <Button
            type="primary"
            icon={<SyncOutlined spin={syncing} />}
            loading={syncing}
            onClick={handleSync}
            style={{ borderRadius: 8 }}
          >
            Sync Now
          </Button>
        </div>

        {listsLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}><Spin /></div>
        ) : (
          <Row gutter={[16, 16]}>
            {PLATFORM_LISTS_ORDER.map(type => {
              const item = platformLists.find(l => l.list_type === type);
              const cfg  = PLAN_LIST_CONFIG[type];
              if (!item) return null;
              return (
                <Col xs={24} sm={8} key={type}>
                  <div style={{
                    borderRadius: 12,
                    border: `1px solid ${cfg.color}33`,
                    background: cfg.bg,
                    padding: '16px 20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      {React.cloneElement(cfg.icon, { style: { color: cfg.color, fontSize: 16 } })}
                      <Text strong style={{ fontSize: 13, color: tk.colorText }}>{item.name}</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 32, fontWeight: 800, color: cfg.color, lineHeight: 1 }}>
                        {item.subscriber_count ?? '—'}
                      </span>
                      <span style={{ fontSize: 13, color: tk.colorTextSecondary }}>subscribers</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                      {item.exists ? (
                        <>
                          <CheckCircleOutlined style={{ color: '#10b981', fontSize: 12 }} />
                          <Text style={{ fontSize: 11, color: '#10b981' }}>
                            {item.synced_at
                              ? `Synced ${new Date(item.synced_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                              : 'List ready'}
                          </Text>
                        </>
                      ) : (
                        <>
                          <ClockCircleOutlined style={{ color: '#f59e0b', fontSize: 12 }} />
                          <Text style={{ fontSize: 11, color: '#f59e0b' }}>Not synced yet — click Sync Now</Text>
                        </>
                      )}
                    </div>

                    <Tooltip title="Create a campaign using this list">
                      <Button
                        size="small"
                        icon={<SendOutlined />}
                        disabled={!item.exists || item.subscriber_count === 0}
                        onClick={() => navigate('/campaigns/create', { state: { preselectedList: item.id } })}
                        style={{ borderRadius: 8, borderColor: cfg.color, color: cfg.color, fontSize: 12 }}
                      >
                        Create Campaign
                      </Button>
                    </Tooltip>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* Recent signups table */}
      <Card
        title="Recent Signups"
        extra={<Button type="link" onClick={() => navigate('/admin/orgs')}>View all →</Button>}
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
      >
        <Table
          dataSource={recentOrgs}
          columns={recentCols}
          rowKey="id"
          pagination={false}
          size="small"
        
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* ── Notification Broadcast ── */}
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BellOutlined style={{ color: '#0a40d9' }} />
            Broadcast Notification
          </span>
        }
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginTop: 24 }}
      >
        <Row gutter={24}>
          {/* Compose form */}
          <Col xs={24} lg={10}>
            <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: tk.colorTextSecondary }}>Send to users</div>
            <Form form={notifForm} layout="vertical" onFinish={sendNotification}>
              <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                <Input placeholder="e.g. Scheduled maintenance on May 15" maxLength={200} />
              </Form.Item>
              <Form.Item name="body" label="Message" rules={[{ required: true, message: 'Message is required' }]}>
                <Input.TextArea rows={3} placeholder="Describe what users need to know…" maxLength={1000} showCount />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="type" label="Type" initialValue="info">
                    <Select>
                      <Select.Option value="info">ℹ️ Info</Select.Option>
                      <Select.Option value="ok">✅ Success</Select.Option>
                      <Select.Option value="warn">⚠️ Warning</Select.Option>
                      <Select.Option value="danger">🚨 Critical</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="target" label="Audience" initialValue="all">
                    <Select>
                      <Select.Option value="all">👥 All users</Select.Option>
                      <Select.Option value="free">🔓 Free users only</Select.Option>
                      <Select.Option value="pro">👑 Pro users only</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={notifSending} block>
                  Broadcast Notification
                </Button>
              </Form.Item>
            </Form>
          </Col>

          {/* Existing notifications list */}
          <Col xs={24} lg={14}>
            <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: tk.colorTextSecondary }}>
              Active notifications
              <Button size="small" type="text" onClick={fetchAdminNotifs} style={{ marginLeft: 8 }}>Refresh</Button>
            </div>
            {notifLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : notifList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: tk.colorTextQuaternary, fontSize: 13 }}>
                No notifications sent yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
                {notifList.map(n => {
                  const dot = { info: '#0a40d9', warn: '#f59e0b', danger: '#ef4444', ok: '#10b981' }[n.type] || '#0a40d9';
                  const badge = { info: 'blue', warn: 'orange', danger: 'red', ok: 'green' }[n.type] || 'blue';
                  return (
                    <div key={n.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      border: `1px solid ${tk.colorBorderSecondary}`,
                      background: tk.colorFillAlter,
                    }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, marginTop: 5, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{n.title}</span>
                          <Tag color={badge} style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>{n.type}</Tag>
                          <Tag style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>
                            { n.target === 'all'  ? '👥 All users'
                            : n.target === 'free' ? '🔓 Free users'
                            : n.target === 'pro'  ? '👑 Pro users'
                            : `Org ${n.target}` }
                          </Tag>
                        </div>
                        <div style={{ fontSize: 12, color: tk.colorTextSecondary, lineHeight: 1.4 }}>{n.body}</div>
                        <div style={{ fontSize: 11, color: tk.colorTextQuaternary, marginTop: 4 }}>
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <Popconfirm
                        title="Delete this notification?"
                        description="Users who haven't dismissed it will no longer see it."
                        onConfirm={() => deleteNotification(n.id)}
                        okText="Delete" okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                      >
                        <Button danger type="text" size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </div>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default AdminDashboard;
